import { Injectable, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireFunctions } from '@angular/fire/functions';
import { User } from 'firebase';
import { auth } from 'firebase/app';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnInit {

  user: User;

  constructor(
    public afAuth: AngularFireAuth,
    public router: Router,
    private firestore: AngularFirestore,
    private fns: AngularFireFunctions
  ) {
    this.ngOnInit();
  }

  ngOnInit() {
    this.afAuth.authState.subscribe(user => {
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }
    });
  }

  async login(email: string, password: string): Promise<User> {
    const result = await this.afAuth.auth.signInWithEmailAndPassword(email, password);
    const uid = result.user.uid;

    const userDoc = await this.firestore.collection('users').doc(uid).get().toPromise();
    const userData = userDoc && userDoc.exists ? userDoc.data() : null;

    const isCustomVerified = userData && userData.verified === true;
    const isLegacyVerified = !!result.user.emailVerified || (userData && userData.emailVerified === true);
    
    if (!isCustomVerified && !isLegacyVerified) {
      throw new Error('Please verify your email using the OTP code first.');
    }
    
    if (!isCustomVerified && isLegacyVerified) {
      await this.firestore.collection('users').doc(uid).set({
        verified: true,
        emailVerified: true,
        updatedAt: new Date()
      }, { merge: true });
    }

    localStorage.setItem('user', JSON.stringify(result.user));
    return result.user;
  }

  async onLogout() {
    await this.afAuth.auth.signOut();
    localStorage.removeItem('user');
    localStorage.removeItem('user-type');
    this.router.navigate(['login']);
  }

  async checkAuth(): Promise<User> {
    return new Promise((resolve) => {
      this.afAuth.authState.subscribe(user => {
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }
        resolve(user as User);
      });
    });
  }

  async sendVerificationMail() {
    if (!this.afAuth.auth.currentUser) {
      return;
    }

    await this.afAuth.auth.currentUser.sendEmailVerification({
      url: 'https://camedu-learning.com/#/verify-email',
    });
  }

  async isUserVerified(uid: string): Promise<boolean> {
    if (!uid) {
      return false;
    }

    const doc = await this.firestore.collection('users').doc(uid).get().toPromise();
    if (doc && doc.exists) {
      const data: any = doc.data();
      if (data && (data.verified === true || data.emailVerified === true)) {
        return true;
      }
    }

    const user = this.afAuth.auth.currentUser;
    return !!(user && user.emailVerified);
  }

  async FacebookAuth(): Promise<auth.UserCredential> {
    const provider = new auth.FacebookAuthProvider();
    return this.afAuth.auth.signInWithPopup(provider);
  }

  async sendResetPasswordMail(email: string): Promise<any> {
    return this.afAuth.auth.sendPasswordResetEmail(email);
  }

  async sendPasswordResetOtp(email: string): Promise<any> {
    const callable = this.fns.httpsCallable('sendPasswordResetOtp');
    return callable({ email }).toPromise();
  }

  async verifyPasswordResetOtp(email: string, code: string): Promise<any> {
    const callable = this.fns.httpsCallable('verifyPasswordResetOtp');
    return callable({ email, code }).toPromise();
  }

  async resetPasswordWithOtp(email: string, code: string, password: string): Promise<any> {
    const callable = this.fns.httpsCallable('resetPasswordWithOtp');
    return callable({ email, code, password }).toPromise();
  }

  async updateAvatarUrl(url: string) {
    if (this.afAuth.auth.currentUser) {
      await this.afAuth.auth.currentUser.updateProfile({
        photoURL: url
      });
    }
  }

  get avatarUrl(): string {
    return this.afAuth.auth.currentUser ? this.afAuth.auth.currentUser.photoURL : '';
  }
}